import React from 'react';
import { Link } from 'react-router-dom';
import { CalendarClock, CreditCard, FileDown, XCircle, HelpCircle, Mail, ArrowLeft } from 'lucide-react';
import SEO from '../components/SEO';

const LAST_UPDATED = 'February 2026';

const Section = ({ icon: Icon, title, children, testId }) => (
  <section className="mb-10" data-testid={testId}>
    <div className="flex items-center gap-2.5 mb-3">
      <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
        <Icon className="w-4.5 h-4.5 text-primary" strokeWidth={2.25} />
      </div>
      <h2 className="text-xl sm:text-2xl font-heading font-semibold tracking-tight">{title}</h2>
    </div>
    <div className="text-[15px] leading-relaxed text-muted-foreground space-y-3 pl-[46px]">
      {children}
    </div>
  </section>
);

const RefundPolicyPage = () => {
  return (
    <>
      <SEO
        title="Cancellation & Refund Policy"
        description="How subscriptions, single-use downloads, cancellations and refunds work at PDF Studio Pro."
      />
      <div className="min-h-[calc(100vh-3.5rem)] bg-background">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-10 sm:pt-14 pb-16">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
            data-testid="refund-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>

          <header className="mb-10 pb-8 border-b border-border" data-testid="refund-page">
            <p className="text-xs uppercase tracking-[0.18em] text-primary font-semibold mb-3">Legal &middot; Billing</p>
            <h1 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight text-foreground mb-3">
              Cancellation &amp; Refund Policy
            </h1>
            <p className="text-sm text-muted-foreground">Last updated: {LAST_UPDATED}</p>
            <p className="text-[15px] text-muted-foreground mt-5 leading-relaxed">
              This page explains how cancellations, trials and refunds work for the PDF Studio Pro
              subscription (R49/month) and single-use document credits (R8 each). Payments are
              processed by Paystack in South African Rand (ZAR).
            </p>
          </header>

          <Section icon={CreditCard} title="Pro Subscription — R49 / month" testId="section-subscription">
            <p>
              The Pro plan renews automatically every 30 days at <strong className="text-foreground">R49</strong>
              until it is cancelled. It unlocks unlimited edits and downloads for as long as it is active.
            </p>
            <p>
              You can <strong className="text-foreground">cancel your subscription at any time</strong> from
              the <Link to="/account" className="text-primary hover:underline">Account &amp; Billing</Link> page —
              no email or support ticket is required.
            </p>
          </Section>

          <Section icon={CalendarClock} title="3-Day Free Trial (R1 card verification)" testId="section-trial">
            <p>
              To start the 3-day free trial we place a non-refundable{' '}
              <strong className="text-foreground">R1 card-verification charge</strong> so we can secure the
              subscription and confirm your payment method is valid.
            </p>
            <p>
              This R1 verification fee is <strong className="text-foreground">not refundable</strong>. It is
              charged once, at the start of the trial, regardless of whether you continue with the
              subscription after the trial ends.
            </p>
          </Section>

          <Section icon={XCircle} title="What happens when you cancel" testId="section-after-cancel">
            <p>
              When you cancel Pro, you keep <strong className="text-foreground">full Pro access until the
              end of your current 30-day billing cycle</strong> — nothing is switched off immediately.
            </p>
            <p>
              After that date your account automatically returns to the free tier (1 download per day)
              and <strong className="text-foreground">no further charges</strong> will be made to your card.
            </p>
          </Section>

          <Section icon={FileDown} title="Single-Use Downloads — R8" testId="section-single-use">
            <p>
              R8 document credits are one-time purchases used to download a single edited PDF without a
              subscription.
            </p>
            <p>
              Once a payment is successful and the document has been{' '}
              <strong className="text-foreground">processed and/or downloaded</strong>, the credit is
              consumed and the purchase is <strong className="text-foreground">non-refundable</strong>.
            </p>
          </Section>

          <Section icon={HelpCircle} title="Refunds" testId="section-refunds">
            <p>
              We <strong className="text-foreground">do not offer partial refunds</strong> for unused days
              within a billing month. If you cancel mid-cycle, Pro access continues until the end of the
              cycle you have already paid for; there is no pro-rated refund for the remaining days.
            </p>
            <p>
              In the rare case of a duplicate charge or a technical error on our side, please contact
              support with your Paystack receipt and we will investigate promptly.
            </p>
          </Section>

          <Section icon={Mail} title="Contact & billing support" testId="section-support">
            <p>
              For any billing question — cancellations, receipts, disputed charges or refund requests —
              email us at{' '}
              <a
                href="mailto:support@pdfstudiopro.co.za"
                className="text-primary hover:underline font-medium"
                data-testid="refund-support-email"
              >
                support@pdfstudiopro.co.za
              </a>{' '}
              and please include the email address on your PDF Studio Pro account and the Paystack
              reference from your receipt so we can find your payment quickly.
            </p>
          </Section>

          <div className="mt-10 pt-6 border-t border-border text-xs text-muted-foreground">
            <p>
              This policy may be updated from time to time. Continued use of PDF Studio Pro after any
              changes are posted here constitutes acceptance of the updated policy.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default RefundPolicyPage;
